import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { ConnectionService } from '../../connection.service';
import { buildAppRoutes } from '../../app.routes';
import { ApiCallsService } from '../../shared/services/api-calls.service';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { LayoutService } from '../../shared/services/layout.service';

@Component({
  selector: 'app-login',
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  isDisappearing = false;
  hasInitialized = false;
  removeBox: boolean = false;
  isLoggingIn: boolean = false;
  loggingInText: string = 'Logging in...';

  ngAfterViewInit() {
    // Allow one tick before animations can start
    setTimeout(() => {
      this.hasInitialized = true;
    });
  }

  checkPageHeight() {
    const height = window.innerHeight;
    this.removeBox = height < 500;
  }

  public show: boolean = false;
  public loginForm: FormGroup;
  currentYear = new Date().getFullYear();
  public rememberMe: boolean = false;
  isOnline = true;
  constructor(
    private readonly fb: FormBuilder,
    public router: Router,
    private readonly auth: AuthService,
    private readonly toastService: ToastService,
    private readonly connectionService: ConnectionService,
    private readonly ApiCallService: ApiCallsService,
    private readonly layout: LayoutService,
    private readonly route: ActivatedRoute,
  ) {
    this.loginForm = this.fb.group({
      Username: ["", [Validators.required]],
      password: ["", [Validators.required]],
    });
  }
  showitem: boolean = false;
  onResize() {
    this.checkPageHeight();
  }
  ngOnInit(): void {
    this.handleDirectLogin();
    this.checkPageHeight();
    window.addEventListener('resize', this.onResize.bind(this));
    this.layout.setMessage('')
    this.connectionService.online$.subscribe((status) => {
      console.log('✅ AppComponent sees status:', status);
    });
    this.showitem = true;
    // Check if active session exists
    const token = localStorage.getItem('token');
    const landingPage = localStorage.getItem('localRedirection') || '/site_summary';

    if (token) {
      // Auto-redirect if token exists
      this.router.navigate([landingPage]);
    } else {
      // Clear session when no active token exists
      localStorage.removeItem('token');
      localStorage.removeItem('profile_name');
      localStorage.removeItem('loginSession');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('login_id');
      localStorage.removeItem('siteList');
      localStorage.removeItem('login_Type');
      sessionStorage.removeItem('consumerSiteId');
      localStorage.removeItem('locationList');
      localStorage.removeItem('site_idLogin');
      localStorage.removeItem('Components');
      localStorage.removeItem('localRedirection');
      localStorage.removeItem('mode');
      localStorage.removeItem('ReadingUnit');
      localStorage.removeItem('sidebarItems');
      localStorage.removeItem('sideMenuList');
      localStorage.removeItem('lastActiveUrl');
      localStorage.removeItem('calculatedExpiry');
      sessionStorage.removeItem('calculatedExpiry');
      localStorage.removeItem('loginTimestamp');
      localStorage.removeItem('live_date');
    }
  }

  showPassword() {
    this.show = !this.show;
  }
  handleDirectLogin(): void {
    const token = this.route.snapshot.queryParamMap.get('d');

    if (!token) return;

    this.auth.directLogin(token).subscribe({
      next: (res: any) => {
        try {
          // assuming API returns base64 string like "username:password"
          const response = res?.data;

          const username = atob(response.name);
          const password = atob(response.password);

          if (!username || !password) {
            throw new Error('Invalid decoded credentials');
          }

          // patch form (reuse existing login flow)
          this.loginForm.patchValue({
            Username: username,
            password: password
          });

          // call existing login function
          this.login();

        } catch (err) {
          console.error('❌ Direct login decode failed', err);
          this.toastService.showToast('Invalid login link', 'colored', 4000, 'danger');
        }
      },
      error: () => {
        this.toastService.showToast('Direct login failed', 'colored', 4000, 'danger');
      }
    });
  }

  login() {
    if (this.loginForm.valid) {
      this.isLoggingIn = true;
      this.loggingInText = 'Authenticating...';

      const formData = new FormData();
      formData.append('username', this.loginForm.value.Username);
      formData.append('password', this.loginForm.value.password);

      this.auth.login(formData).subscribe({
        next: (response) => {
          this.loggingInText = 'Setting up your workspace...';
          localStorage.setItem('token', response.Data.token);
          localStorage.setItem('profile_name', response.Data.profile_name);
          localStorage.setItem('login_id', response.Data.login_id);
          localStorage.setItem('loginSession', response.Data.expiry_time);
          localStorage.setItem('login_count', response.Data.login_count);
          localStorage.setItem('SiteName', response.Data.site_name);
          localStorage.setItem('login_Type', response.Data.type); // IMPORTANT: must be set before routes are built
          localStorage.setItem('live_date', response.Data?.live_date);
          localStorage.setItem('localRedirection', response.Data.landing_page);

          let siteValue = response.Data.site_id;
          if (siteValue && siteValue.includes(',')) {
            siteValue = 'ALL';
          }
          const siteid = btoa(siteValue || 'ALL');
          localStorage.setItem('site_idLogin', siteid || 'ALL');
          localStorage.setItem('textchange', JSON.stringify(response.Data.asset_list));
          localStorage.setItem('mode', response.Data.mode || 'Both');
          localStorage.setItem('TypesOfLogin', JSON.stringify(response.Data.login_type_list));

          // 🟢 Step 2: Rebuild routes using the correct login_Type
          this.router.resetConfig(buildAppRoutes());

          // 🟢 Step 3: Session calculation
          const expirySeconds = parseInt(response.Data.expiry_time, 10);
          const loginTimestamp = new Date();
          localStorage.setItem('loginTimestamp', loginTimestamp.toISOString());

          if (!isNaN(expirySeconds) && expirySeconds > 0) {
            const expiryDate = new Date(loginTimestamp.getTime() + expirySeconds * 1000);
            localStorage.setItem('calculatedExpiry', expiryDate.toISOString());
            sessionStorage.setItem('calculatedExpiry', expiryDate.toISOString());
          } else {
            localStorage.removeItem('calculatedExpiry');
            sessionStorage.removeItem('calculatedExpiry');
          }

          if (this.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          } else {
            localStorage.removeItem('rememberMe');
          }

          localStorage.removeItem('sidebarItems');
          localStorage.removeItem('sideMenuList');
          localStorage.removeItem('lastActiveUrl');

          // 🟢 Step 4: Fetch prerequisite background APIs before navigating
          const tasks: Observable<any>[] = [
            this.ApiCallService.getCategories().pipe(
              tap(res => {
                localStorage.setItem('role_categories', JSON.stringify(res?.Data ?? res ?? []));
              }),
              catchError(() => of(null))
            ),
            this.DashboardComponents(response.Data.type).pipe(
              catchError(() => of(null))
            )
          ];

          if ((response.Data.category === '2' || (siteValue && siteValue !== 'ALL')) && siteValue) {
            tasks.push(
              this.ApiCallService.getLocationList(siteValue).pipe(
                tap(res => {
                  localStorage.setItem('locationList', JSON.stringify(res?.Data ?? res ?? []));
                }),
                catchError(() => of(null))
              )
            );
          }

          forkJoin(tasks).subscribe({
            next: () => {
              this.isDisappearing = true;
              setTimeout(() => {
                this.isLoggingIn = false;
                this.toastService.showToast(
                  `Welcome ${response.Data.profile_name}!`,
                  'colored',
                  5000,
                  'success'
                );
                this.router.navigate([response.Data.landing_page]);
              }, 655);
            },
            error: () => {
              this.isDisappearing = true;
              setTimeout(() => {
                this.isLoggingIn = false;
                this.router.navigate([response.Data.landing_page]);
              }, 655);
            }
          });
        },
        error: () => {
          this.isLoggingIn = false;
          this.toastService.showToast(
            `Username or password is wrong`,
            'colored',
            5000,
            'danger'
          );
        },
      });
    }
  }

  DashboardComponents(LoginType: string = '') {
    return this.ApiCallService.getProjectComponents(LoginType).pipe(
      tap(res => {
        const dashboardData = res?.Data?.list?.dashboard ?? res?.Data ?? res;
        if (dashboardData) {
          localStorage.setItem('Components', JSON.stringify(dashboardData));
        }
      })
    );
  }

  ngOnDestroy() {
    // Clean up the event listener
    window.removeEventListener('resize', this.onResize.bind(this));
  }
}

